import { useEffect, useState, useCallback, useMemo } from "react";
import { USER_TYPES } from "../utils";
import { validateUserForm, getTodayISO } from "../utils";

export function useUserForm(user, onSubmit, onCancel) {
  const initialFormData = useMemo(
    () => ({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      activity_start_date: getTodayISO(),
      activity_end_date: "",
      user_type: USER_TYPES.STUDENT,
    }),
    [],
  );

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Update form when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        email: user.email || "",
        activity_start_date: user.activity_start_date || getTodayISO(),
        activity_end_date: user.activity_end_date || "",
        user_type: user.user_type || USER_TYPES.STUDENT,
      });
    } else {
      resetForm();
    }
  }, [user]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const validateForm = useCallback(() => {
    const validation = validateUserForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }
    setErrors({});
    return true;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setErrors({});
      if (!validateForm()) return;

      try {
        setSubmitting(true);
        if (onSubmit) await onSubmit(formData);
      } catch (err) {
        setErrors({
          submit:
            err.message || "Une erreur est survenue lors de la sauvegarde",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [formData, onSubmit, validateForm],
  );

  const handleCancel = useCallback(() => {
    setErrors({});
    if (onCancel) onCancel();
  }, [onCancel]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
  }, [initialFormData]);

  const userTypeOptions = useMemo(() => Object.values(USER_TYPES), []);

  return {
    formData,
    errors,
    submitting,
    userTypeOptions,
    handleChange,
    handleSubmit,
    handleCancel,
    validateForm,
    resetForm,
    setErrors,
    setFormData,
  };
}
